"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/hooks/use-translation";
import type { Translate } from "@/lib/i18n/dictionary";
import DeviceCluster from "./hero-slides/device-cluster";
import RevenueDashboard from "./hero-slides/revenue-dashboard";
import CrmProfile from "./hero-slides/crm-profile";

/**
 * The three stories the hero rotates through — each brings its own copy for
 * the left column, so the whole fold tells one story at a time.
 *
 * `runtime` is how long that slide's own entrance takes to finish, in ms — the
 * chart's draw, the CRM cards landing — read off the keyframes in
 * `globals.css`. The reel used to move on at `runtime + HOLD` so that no slide
 * sat there finished and waiting; it now runs on a flat SLIDE_MS instead, by
 * request. `runtime` is kept because it is still the honest figure for how
 * long each entrance takes, and it is what tells you whether SLIDE_MS is long
 * enough to let one finish — the longest is 2550ms against a 3000ms hold, so
 * every slide settles with a beat to spare. Keep these in step if the
 * keyframes change.
 */
const SLIDES = [
  {
    key: "devices",
    runtime: 2550,
    // Three lines, and the breaks are unconditional rather than `sm:` — the
    // comp draws the same three on a phone as on a desktop, and each one is a
    // unit worth keeping whole: what it is, which two products, who it is for.
    // Thai sets no spaces between words, so letting this wrap on its own gives
    // ragged breaks mid-phrase instead of the reading rhythm the comp has.
    //
    // Note this supersedes the keyword pass (SEO brief, Aug 2026), which had
    // put "ระบบ" immediately before each product word so the H1 carried the
    // exact phrases ระบบ POS / ระบบ CRM. The new copy drops it; those phrases
    // now live only in the page <title>.
    heading: (t: Translate) => (
      <>
        {t("home.hero.devices.heading.a")}
        <br />
        <span className="text-[#0084EB]">POS</span>{" "}
        {t("home.hero.devices.heading.and")}{" "}
        <span className="text-[#0084EB]">CRM</span>
        <br />
        {t("home.hero.devices.heading.b")}
      </>
    ),
    lead: (t: Translate) => (
      <>
        {t("home.hero.devices.lead.a")}
        <br className="hidden sm:block" /> {t("home.hero.devices.lead.b")}
      </>
    ),
  },
  {
    key: "revenue",
    runtime: 2350,
    heading: (t: Translate) => (
      <>
        {t("home.hero.revenue.heading.a")}
        <br className="hidden sm:block" /> {t("home.hero.revenue.heading.with")}
        <span className="text-[#0084EB]">
          {t("home.hero.revenue.heading.highlight")}
        </span>
      </>
    ),
    lead: (t: Translate) => (
      <>
        {t("home.hero.revenue.lead.a")}
        <br className="hidden sm:block" /> {t("home.hero.revenue.lead.b")}
      </>
    ),
  },
  {
    key: "crm",
    runtime: 2300,
    heading: (t: Translate) => (
      <>
        {t("home.hero.crm.heading.a")}
        <br className="hidden sm:block" /> {t("home.hero.crm.heading.b")}
      </>
    ),
    lead: (t: Translate) => (
      <>
        {t("home.hero.crm.lead.a")}
        <br className="hidden sm:block" /> {t("home.hero.crm.lead.b")}
      </>
    ),
    // The comp breaks this heading as exactly two lines, and 38px is the
    // largest size whose first segment fits the 533px copy column (42px runs
    // 8px over), so it rides a slightly smaller clamp than the other slides.
    headingClass: "lg:text-[clamp(30px,2.64vw,38px)]/[1.4]",
  },
] as const;

/** How long each slide holds before the reel moves on — one flat interval for
    every slide, rather than a beat measured from each one's own entrance.

    Each slide's own pieces are staggered to fill this window rather than
    landing in the first second and waiting (see `runtime` above): the scene is
    still assembling itself right up to the last few hundred milliseconds, so
    the reel reads as continuous motion instead of three short bursts with a
    pause after each. Shortening this without also pulling those staggers in
    would cut a slide off mid-entrance. */
const SLIDE_MS = 3000;

/** How long a reader-driven move (drag, arrow key) holds autoplay before the
    rotation carries on. */
const HOLD_AFTER_TAKE = 3000;

/** Past this much horizontal travel, letting go changes slide. */
const SWIPE_PX = 48;

/**
 * Hero carousel — both columns of the hero fold. The copy block and the
 * product-shot stage swap together, driven by one index.
 *
 * This renders as two siblings (a fragment) directly into the hero's grid.
 *
 * Slides are stacked in one grid cell and cross-faded rather than slid along a
 * track. A track would need `overflow: hidden` on the carousel itself, and the
 * hero's whole composition depends on bleeding past its own column — the
 * tablet on slide 1 sits at `right-[-10%]`, and the section clips it at the
 * *viewport* edge, not the column's. Fading, plus each slide's own pieces
 * flying in, carries the movement instead. Stacking also fixes the height, so
 * nothing below the hero shifts as it rotates.
 *
 * Entrances restart on every rotation without remounting anything, including
 * the copy column: the stage's keyframes hang off `[data-active="true"]`, so
 * flipping the attribute off removes the animation and flipping it back on
 * starts it again from the top; the copy column's entrance classes restart the
 * same way, via a forced-reflow reset (see the effect below) rather than a
 * `key` remount, which spares three subtrees a full unmount/mount every 3s.
 *
 * The reel rotates on its own from the moment it mounts. It used to wait for
 * the visitor's first interaction, to keep every rotation out of the page's
 * LCP measurement; that gate was removed by request, and the autoplay effect
 * below records what it cost.
 *
 * The reader can take over two ways — arrow keys, or grabbing the stage
 * (pointer events, so mouse drag and touch swipe are the same code). Doing
 * either holds autoplay for HOLD_AFTER_TAKE, then the rotation carries on —
 * a temporary hold rather than the comp's permanent pauseOnInteraction,
 * by request. Hover also pauses; leaving resumes. Focus deliberately does
 * not pause — clicking or dragging leaves the stage focused, and a focus
 * pause would silently turn every mouse user's 3-second hold into a
 * permanent stop. Keyboard users get a fresh hold with each arrow press.
 *
 * On page load this hydrates showing exactly what the server rendered (slide
 * 1, copy included), so the LCP heading paints from SSR HTML and is simply
 * adopted — the lesson from the v1 hero still holds.
 */
export default function HeroCarousel() {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  /** True while a reader-driven move is holding autoplay (see `hold`). */
  const [held, setHeld] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  /** False until the page has loaded; see the warm-up effect below. Only the
      two idle slides' mounting waits on it — the reel itself does not. */
  const [warmed, setWarmed] = useState(false);
  /** Live horizontal offset while dragging, in px. 0 whenever we aren't. */
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  /** Null between gestures. `axis` stays null until the drag commits to one. */
  const drag = useRef<{
    id: number;
    x: number;
    y: number;
    axis: "x" | "y" | null;
  } | null>(null);

  const holdTimer = useRef<number>(undefined);

  const slide = SLIDES[index];

  const go = useCallback((next: number) => {
    setIndex(((next % SLIDES.length) + SLIDES.length) % SLIDES.length);
  }, []);

  /** Pause autoplay for HOLD_AFTER_TAKE from now; re-taking restarts it. */
  const hold = useCallback(() => {
    setHeld(true);
    clearTimeout(holdTimer.current);
    holdTimer.current = window.setTimeout(
      () => setHeld(false),
      HOLD_AFTER_TAKE,
    );
  }, []);

  useEffect(() => () => clearTimeout(holdTimer.current), []);

  /** A reader-driven move: go there, and give the reader time to look. */
  const take = useCallback(
    (next: number) => {
      hold();
      go(next);
    },
    [go, hold],
  );

  // Don't run the reel while it is scrolled out of view — it would replay the
  // chart draw at nobody, and each replay is a fresh compositor pass.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /**
   * Wake the two idle slides up once the page has finished loading.
   *
   * This is not a gate on the rotation — the reel starts on its own (see the
   * autoplay effect below). It only decides when slides 2 and 3 are allowed
   * into the DOM, because all three are stacked in the same grid cell and the
   * browser counts the idle ones as in-viewport: mounting them with slide 1
   * means ~70 KiB of product shots being fetched inside the load window,
   * against the CSS and JS the fold actually needs. Waiting for `load` and
   * then an idle slot puts that fetch after the critical path but comfortably
   * before the first rotation at SLIDE_MS.
   *
   * A reader who drags or arrows straight to slide 2 does not wait for any of
   * this — see the `i === index` clause where the slides are rendered.
   */
  useEffect(() => {
    let idle: number | undefined;
    const warm = () => {
      const ric = window.requestIdleCallback;
      idle = ric
        ? ric(() => setWarmed(true), { timeout: 1000 })
        : window.setTimeout(() => setWarmed(true), 200);
    };
    if (document.readyState === "complete") {
      warm();
      return () => window.clearTimeout(idle);
    }
    window.addEventListener("load", warm, { once: true });
    return () => {
      window.removeEventListener("load", warm);
      window.clearTimeout(idle);
    };
  }, []);

  /**
   * Autoplay.
   *
   * This used to be gated behind the visitor's first interaction, and the
   * reason is worth keeping on record before anyone re-adds it. Every rotation
   * repaints the whole copy column — heading, lead and CTA label all change —
   * and a newly painted text block is a fresh LCP candidate. Chrome keeps
   * accepting new candidates right up until the first user interaction and not
   * a moment sooner, so on an untouched page the reel keeps moving the
   * "largest paint" mark forward at every swap, forever. That is exactly the
   * condition Lighthouse runs in: mobile scored the fold at LCP 5.2s — the
   * first autoplay swap at the time — for a heading its own lcp-breakdown put
   * on screen at 713ms. Field data from anyone who lands and reads without
   * touching anything reads the same way.
   *
   * Two things that do not fix it, also worth recording: waiting for `load`
   * before the first rotation only moves the problem later, and keeping the
   * <h1> element itself mounted (see the entrance-replay effect below) does
   * not help either — its *children* are entirely new nodes on every slide,
   * and that is enough for Chrome to record a new text paint against the same
   * element.
   *
   * The gate is gone by request: a hero that sits still until it is touched
   * does not tell a first-time reader there are three stories in it, and that
   * was judged the more expensive trade. So the cost is real and it is
   * accepted — expect LCP to be reported at roughly the first swap, SLIDE_MS
   * after paint, in both Lighthouse and CrUX. Lengthening SLIDE_MS moves that
   * number directly; restoring the gate is the only thing that removes it.
   */
  useEffect(() => {
    if (held || hovered || dragging || !onScreen) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = window.setTimeout(() => go(index + 1), SLIDE_MS);
    return () => clearTimeout(t);
  }, [index, held, hovered, dragging, onScreen, go]);

  /**
   * Replay the copy column's entrance on every slide change, without ever
   * unmounting the <h1>.
   *
   * This used to be `key={slide.key}` on the wrapping div, remounting the
   * whole column so its `.jd-fade-up` entrance replayed. That created a
   * brand-new <h1> element on every rotation — and the LCP spec keeps
   * accepting new candidates for as long as the page has never been
   * interacted with, which a Lighthouse lab run never does. So whichever
   * slide happened to be on screen when the trace ended got reported as the
   * page's Largest Contentful Paint, at *that* rotation's timestamp — e.g.
   * mobile LCP measured at ~load+5s (the first autoplay swap) for a heading
   * that had actually been on screen and readable since ~1.7s.
   *
   * Keeping the same <h1> node and only updating its text (React's normal
   * reconciliation, no `key`) means later rotations don't register as new
   * LCP candidates at all. The replay itself is done by forcing the
   * `.jd-fade-up` keyframe to restart on the existing nodes: set
   * `animation: none` inline, force a reflow so the browser drops the
   * previous animation instance, then clear the inline override so the
   * class-defined keyframe re-applies as new. Same trick the stage slides
   * already use via `[data-active="true"]` newly matching — a rule starting
   * to match (or, here, a cleared `animation: none`) is what restarts a CSS
   * animation; touching `className`/`textContent` alone does not.
   */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // initial mount already gets the entrance via `backwards` fill
    }
    const el = copyRef.current;
    if (!el) return;
    // Both entrance classes: the copy column is `.jd-fade-up` throughout
    // except the <h1>, which rides the opacity-free `.jd-rise` so it does not
    // pay 600ms of LCP for its reveal (see globals.css).
    const entrances = el.querySelectorAll<HTMLElement>(".jd-fade-up, .jd-rise");
    entrances.forEach((node) => {
      node.style.animation = "none";
    });
    void el.offsetWidth; // force reflow between the reset and the restore
    entrances.forEach((node) => {
      node.style.animation = "";
    });
  }, [index]);

  const endDrag = useCallback(
    (commit: boolean) => {
      const d = drag.current;
      drag.current = null;
      setDragging(false);
      setDx(0);
      if (!d || d.axis !== "x") return;
      if (commit && Math.abs(dx) > SWIPE_PX) take(index + (dx < 0 ? 1 : -1));
      else if (commit) hold(); // a deliberate grab, even if too short
    },
    [dx, index, take, hold],
  );

  return (
    <>
      {/* Copy block. Not keyed on the slide — see the entrance-replay effect
          above for why: the <h1> node has to survive every rotation so later
          swaps don't keep resetting the page's LCP. The entrance cascade
          still replays on each swap, just via a forced animation restart on
          the same nodes instead of a remount. */}
      {/* Centred until the grid splits into two columns at lg, per the phone
          comp — the copy is the whole fold there, with the stage stacked
          beneath it, and a left-ragged block in the middle of the screen reads
          as misplaced rather than deliberate. From lg it is the left column of
          a two-column composition again, so it goes back to ragged-right. */}
      <div
        ref={copyRef}
        className="mx-auto max-w-[560px] text-center lg:mx-0 lg:text-left"
      >
        {/* Two clamps, same reason at both ends: hold the comp's line breaks
            rather than the comp's exact type size.

            Phone end — the comp draws this at 28px, but its longest line
            ("ที่เข้าใจธุรกิจคุณอย่างแท้จริง") measures 348px there against the
            331px a 375px screen actually offers once the side padding and the
            reserved scrollbar are out. At 28px it wrapped and left "แท้จริง"
            orphaned on a fourth line, which is the one thing the hard breaks
            exist to prevent. 6.9vw is the steepest slope that still fits that
            line from 320px up; the 28px ceiling means anything from ~406px
            wide gets the comp's size unchanged.

            lg end — same idea for the desktop range: a fixed 42px wraps the
            long lines at 1280. */}
        <h1
          className={`jd-rise text-[clamp(22px,6.9vw,28px)]/[1.22] font-bold text-gray-900 [text-wrap:balance] sm:text-[36px]/[1.4] ${
            ("headingClass" in slide && slide.headingClass) ||
            "lg:text-[clamp(34px,2.92vw,42px)]/[1.4]"
          }`}
        >
          {slide.heading(t)}
        </h1>

        {/* #666666 comes off the comp; the gray-600 it replaces was eyeballed
            back when the Figma connector was unauthorized (see hero.tsx). */}
        <p
          className="jd-fade-up mt-3 max-w-[46ch] text-[18px]/[24px] text-[#666666] sm:mt-5 sm:text-lg/[1.7]"
          style={{ animationDelay: "0.12s" }}
        >
          {slide.lead(t)}
        </p>

        {/* Both actions land on the contact section at the foot of the page
            (`#contact` — "เริ่มต้นวันนี้ เราพร้อมโตไปกับธุรกิจคุณ"), same as
            the navbar's CTA. v2 has no sign-up flow for the trial button to
            go to, so talking to us *is* the conversion; point it somewhere
            else once there is one. data-track keeps both reporting to the CMS
            click dashboard. */}
        <div
          className="jd-fade-up mt-5 flex flex-wrap items-center gap-2 lg:mt-8 lg:gap-4"
          style={{ animationDelay: "0.24s" }}
        >
          {/* Two buttons at two sizes. The phone comp gives the trial CTA the
              full width of the fold in the navbar's #0084EB at a 14px radius —
              flat, 18/24 at weight 500. From lg it takes the desktop comp's
              shorter pill beside the text link, so the box, radius and type
              are all unwound there rather than the mobile case being the
              exception.

              The *fill* is the one figure that does not unwind. The desktop
              comp draws this pill in #2D6BF0, a visibly more indigo blue than
              the #0084EB it wears on a phone — and lg is exactly the width
              where the navbar CTA appears, so the two blue buttons on the fold
              ended up as two different blues sitting a few hundred pixels
              apart. Brand blue wins over the comp here: one blue button colour
              across the whole site. */}
          <Button
            asChild
            className="h-auto flex-1 rounded-[14px] bg-primary-strong px-6 py-2 text-[18px]/[24px] font-medium text-white transition-transform hover:scale-[1.03] hover:bg-primary-strong/90 lg:py-2 lg:flex-none lg:rounded-lg lg:px-6 lg:text-base lg:font-semibold"
          >
            <a href="#contact" data-track="hero_trial">
              {t(`home.hero.${slide.key}.cta`)}
            </a>
          </Button>
          {/* Secondary action is a text link in the comp, not a filled button
              — a plain <a> (not the Button primitive) so none of its
              background/height styles need unwinding. The phone comp keeps
              only the blue button, so this stands down below lg; #contact is
              still one tap away through the drawer's CTA. */}
          <a
            href="#contact"
            data-track="hero_contact"
            className="hidden items-center justify-center gap-2.5 rounded-[14px] px-5 py-2 text-[18px]/[24px] font-medium text-[#0060A8] underline underline-offset-4 transition-colors hover:text-[#004C86] lg:inline-flex"
          >
            {t("home.hero.contactLink")}
          </a>
        </div>
      </div>

      {/* The copy for the slides that are not currently showing.

          The column above deliberately keeps ONE <h1> and swaps its text (see
          the entrance-replay effect) — which means the headings and leads for
          slides 2 and 3 were never in the HTML the server sent. Googlebot
          renders and would eventually rotate to them; the crawlers behind AI
          answers do not run JS at all, so two thirds of the fold's copy simply
          did not exist for them.

          So it is mirrored here, in the DOM from the first byte and
          `display: none` until its slide comes round — at which point the
          visible column has it and this block drops it, so the same words are
          never on screen twice. `hidden` (not opacity or off-screen
          positioning) is what keeps this out of the LCP running: a
          `display: none` subtree is never painted and never a paint candidate,
          so the fold's measured LCP is unchanged.

          Headings render as <p>, not <h1> — one <h1> per page, and the visible
          column owns it. */}
      <div hidden>
        {SLIDES.map((s, i) =>
          i === index ? null : (
            <div key={s.key}>
              <p>{s.heading(t)}</p>
              <p>{s.lead(t)}</p>
              <p>{t(`home.hero.${s.key}.cta`)}</p>
            </div>
          ),
        )}
      </div>

      {/* Stage column. Wider than its grid track so the shots bleed past the
          viewport edge like the comp; the section's overflow-hidden crops
          them there. */}
      <div
        className="jd-fade-up relative w-full lg:w-[112%]"
        style={
          {
            animationDelay: "0.3s",
            "--jd-fade-up-from": "28px",
          } as React.CSSProperties
        }
      >
        {/* Decorative layers from the comp, under every slide: a rotated blue
            glow and a soft gray ground-shadow ellipse. Sized against a box
            that recreates the original single-shot wrapper (whose height came
            from the 78%-wide laptop, ratio 2088/1309 → aspect ≈ 2.045), not
            against the stage — the stage is taller, and letting the glow grow
            with it deepened the blue noticeably. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-1/2 aspect-[2045/1000] -translate-y-1/2"
        >
          <div className="absolute left-[40%] top-[10%] h-[80%] w-[42%] -rotate-[29deg] rounded-full bg-[rgba(0,134,240,0.75)] blur-[100px]" />
          <div className="absolute bottom-[-22%] left-[2%] h-[45%] w-full rounded-full bg-[radial-gradient(ellipse_50%_50%_at_50%_50%,#C1C1C1_0%,rgba(193,193,193,0)_100%)]" />
        </div>

        {/* One grid cell, three slides stacked in it.

            From sm up the ratio is the dashboard screenshot's own, which slide
            2 needs — it has to fill the stage exactly or its chart overlay
            loses registration. Below sm it takes slide 3's squarer frame
            ratio instead: at 331px wide the screenshot ratio left the stage
            only 246px tall, which squeezed the CRM composition well under the
            size it reads at. Slide 2 just centres in the taller box. */}
        <div
          ref={stageRef}
          role="group"
          aria-roledescription="carousel"
          aria-label={t("home.hero.stageLabel")}
          // The stage itself is the only control (no dots in the comp), so it
          // has to be reachable by keyboard for the arrow-key handler below.
          tabIndex={0}
          className={`hero-stage grid aspect-[804/788] w-full cursor-grab select-none grid-cols-1 grid-rows-1 active:cursor-grabbing sm:aspect-[2388/1776] lg:aspect-auto lg:h-[calc(100dvh-8.25rem)] ${
            dragging ? "is-dragging" : ""
          }`}
          style={{
            transform: dx ? `translate3d(${dx}px, 0, 0)` : undefined,
            transition: dragging
              ? "none"
              : "transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") take(index + 1);
            else if (e.key === "ArrowLeft") take(index - 1);
            else return;
            e.preventDefault();
          }}
          // Native image dragging would hijack the gesture before we see it.
          onDragStart={(e) => e.preventDefault()}
          onPointerDown={(e) => {
            if (e.pointerType === "mouse" && e.button !== 0) return;
            drag.current = {
              id: e.pointerId,
              x: e.clientX,
              y: e.clientY,
              axis: null,
            };
          }}
          onPointerMove={(e) => {
            const d = drag.current;
            if (!d || d.id !== e.pointerId) return;
            const mx = e.clientX - d.x;
            const my = e.clientY - d.y;
            if (!d.axis) {
              // Wait for the gesture to declare itself. A vertical one is the
              // page scrolling (`touch-action: pan-y` lets the browser keep
              // it), so we bow out rather than fight for it.
              if (Math.max(Math.abs(mx), Math.abs(my)) < 8) return;
              if (Math.abs(my) > Math.abs(mx)) {
                drag.current = null;
                return;
              }
              d.axis = "x";
              setDragging(true);
              e.currentTarget.setPointerCapture(e.pointerId);
            }
            // Damped, so the stage gives a little without sliding right off.
            setDx(Math.max(-140, Math.min(140, mx * 0.55)));
          }}
          onPointerUp={() => endDrag(true)}
          onPointerCancel={() => endDrag(false)}
        >
          {/* Slides 2 and 3 hold off until the page has loaded — see the
              warm-up effect above for why their images must not be fetched
              inside the load window. `i === index` is the escape hatch: a
              reader who drags or arrows to a slide gets it immediately,
              warmed or not. The stage's own aspect-ratio classes fix its
              height, so nothing shifts when they arrive. */}
          {SLIDES.map((s, i) => {
            if (i !== 0 && !warmed && i !== index) return null;
            return (
              <div
                key={s.key}
                data-active={i === index}
                // `inert` keeps the two idle slides out of the tab order and the
                // accessibility tree; they stay mounted so the stage keeps its
                // height and their images stay decoded between visits.
                inert={i !== index}
                role="group"
                aria-roledescription="slide"
                aria-label={t("home.hero.slideAria", {
                  index: i + 1,
                  total: SLIDES.length,
                  label: t(`home.hero.${s.key}.label`),
                })}
                className="hero-slide col-start-1 row-start-1 flex h-full items-center"
              >
                {i === 0 ? (
                  <DeviceCluster />
                ) : i === 1 ? (
                  <RevenueDashboard />
                ) : (
                  <CrmProfile />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
